"use client";

import { Button } from "@/components/ui/button";
import {
  BottomSheet,
  BottomSheetContent,
  BottomSheetDescription,
  BottomSheetDragRegion,
  BottomSheetTitle,
} from "@/components/ui/bottom-sheet";
import { useListMyClubForm } from "@/hooks/useListMyClubForm";
import { useTranslations } from "next-intl";
import { ClubTypeSelector, ListMyClubFormFields } from "./ListMyClubFormFields";

interface ListMyClubBottomSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function SuccessView({ onClose }: { onClose: () => void }) {
  const t = useTranslations("listMyClub");

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-6 pb-6 text-center">
      <BottomSheetDragRegion className="flex flex-col items-center">
        <div className="mx-auto mb-4 text-6xl" aria-hidden="true">
          🏆
        </div>
        <h2 className="text-xl font-semibold">{t("successTitleMobile")}</h2>
      </BottomSheetDragRegion>
      <p className="mt-2 text-sm text-muted-foreground">
        {t("successMessageMobile")}
      </p>
      <Button onClick={onClose} className="mt-6 w-full h-12 rounded-full">
        {t("done")}
      </Button>
    </div>
  );
}

export function ListMyClubBottomSheet({
  open,
  onOpenChange,
}: ListMyClubBottomSheetProps) {
  const t = useTranslations("listMyClub");
  const form = useListMyClubForm(onOpenChange);
  const isSubmitting = form.status === "submitting";

  if (form.status === "success") {
    return (
      <BottomSheet open={open} onOpenChange={form.handleClose}>
        <BottomSheetContent
          open={open}
          onOpenChange={form.handleClose}
          className="h-[85vh]"
        >
          <SuccessView onClose={form.handleClose} />
        </BottomSheetContent>
      </BottomSheet>
    );
  }

  return (
    <BottomSheet open={open} onOpenChange={form.handleClose}>
      <BottomSheetContent
        open={open}
        onOpenChange={form.handleClose}
        className="h-[85vh]"
      >
        <BottomSheetDragRegion className="px-6">
          <BottomSheetTitle className="text-left text-lg">
            {t("title")}
          </BottomSheetTitle>
          <BottomSheetDescription className="text-left mt-1">
            {t("description")}
          </BottomSheetDescription>
        </BottomSheetDragRegion>

        <form
          onSubmit={(e) =>
            form.handleSubmit(e, t("contactRequired"), t("roleRequired"))
          }
          className="flex flex-col flex-1 min-h-0"
        >
          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto px-6">
            <div className="mt-4">
              <ClubTypeSelector
                clubType={form.formData.clubType}
                onClubTypeChange={(v) => form.handleFieldChange("clubType", v)}
              />
            </div>

            <div className="mt-4 space-y-4">
              <ListMyClubFormFields
                formData={form.formData}
                isSubmitting={isSubmitting}
                hasError={form.status === "error"}
                contactError={form.contactError}
                roleError={form.roleError}
                errorMessage={form.errorMessage}
                idPrefix="mobile"
                onChange={form.handleChange}
                onRoleChange={(v) => form.handleFieldChange("role", v)}
                onContactPreferenceChange={(v) =>
                  form.handleFieldChange("contactPreference", v)
                }
                onReferralChange={(v) =>
                  form.handleFieldChange("referralSource", v)
                }
              />
            </div>
          </div>

          {/* Fixed footer */}
          <div className="shrink-0 px-6 py-4 space-y-2 border-t">
            <Button
              type="submit"
              className="w-full h-12 rounded-full"
              disabled={isSubmitting}
            >
              {isSubmitting ? t("sending") : t("submit")}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full h-12 rounded-full"
              onClick={form.handleClose}
              disabled={isSubmitting}
            >
              {t("cancel")}
            </Button>
          </div>
        </form>
      </BottomSheetContent>
    </BottomSheet>
  );
}
